import React, { useEffect, useState } from 'react';
import { useTheme } from '../../ThemeContext';
import { Folder, File, Upload, ChevronRight, Home, RefreshCw, Trash2, FolderPlus, Move, Copy, MoreVertical, Download } from 'lucide-react';
import { API_URL } from '../../types';

interface FileInfo {
  name: string;
  path: string;
  is_directory: boolean;
  size?: number;
  modified_time?: string;
}

interface DirectoryListing {
  path: string;
  items: FileInfo[];
  parent_path?: string;
}

export default function FilesPage() {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [directoryListing, setDirectoryListing] = useState<DirectoryListing | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'file' | 'directory'>('file');
  const [uploadTargetFolder, setUploadTargetFolder] = useState<string>('');
  
  // New folder dialog state
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [createFolderError, setCreateFolderError] = useState<string | null>(null);
  
  // Move/rename dialog state
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveSourceItem, setMoveSourceItem] = useState<FileInfo | null>(null);
  const [moveDestPath, setMoveDestPath] = useState('');
  const [moveNewName, setMoveNewName] = useState('');
  const [moveError, setMoveError] = useState<string | null>(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState<FileInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Action menu state
  const [activeMenuItemPath, setActiveMenuItemPath] = useState<string | null>(null);

  useEffect(() => {
    loadDirectory(currentPath);
    // Reset upload target folder when directory changes
    setUploadTargetFolder('');
  }, [currentPath]);

  const loadDirectory = async (path: string) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/files?path=${encodeURIComponent(path)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load directory');
      }

      const data = await response.json();
      setDirectoryListing(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load directory');
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path: string) => {
    setCurrentPath(path);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (uploadMode === 'file') {
        // Single file mode - collect all selected files
        const filesArray = Array.from(e.target.files);
        setUploadFiles(filesArray);
        setUploadFile(null);
      } else {
        // Directory mode - collect all files
        const filesArray = Array.from(e.target.files);
        setUploadFiles(filesArray);
        setUploadFile(null);
      }
      setUploadError(null);
      setUploadSuccess(null);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Determine the actual target path (current path + selected folder)
      const actualTargetPath = uploadTargetFolder 
        ? (currentPath ? `${currentPath}/${uploadTargetFolder}` : uploadTargetFolder)
        : currentPath;

      if (uploadMode === 'file') {
        // Single/Multiple file upload - upload each file to the same directory
        let successCount = 0;
        let failCount = 0;

        for (const file of uploadFiles) {
          try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/files/upload?path=${encodeURIComponent(actualTargetPath)}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
              body: formData,
            });

            if (response.ok) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (failCount > 0) {
          setUploadSuccess(`Uploaded ${successCount} file(s) successfully. ${failCount} file(s) failed.`);
        } else {
          setUploadSuccess(`Successfully uploaded ${successCount} file(s).`);
        }
        setUploadFiles([]);
      } else {
        // Directory upload - upload each file with its relative path
        let successCount = 0;
        let failCount = 0;

        for (const file of uploadFiles) {
          try {
            // Extract relative path from webkitRelativePath
            const relativePath = (file as any).webkitRelativePath || file.name;
            const pathParts = relativePath.split('/');
            
            // Remove the first part (root folder name) and get the rest
            const filePathWithinDir = pathParts.slice(1).join('/');
            
            // Determine the target directory path
            let targetPath = actualTargetPath;
            if (filePathWithinDir.includes('/')) {
              const dirPath = filePathWithinDir.substring(0, filePathWithinDir.lastIndexOf('/'));
              targetPath = actualTargetPath ? `${actualTargetPath}/${dirPath}` : dirPath;
            }
            
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/files/upload?path=${encodeURIComponent(targetPath)}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
              },
              body: formData,
            });

            if (response.ok) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (failCount > 0) {
          setUploadSuccess(`Uploaded ${successCount} files successfully. ${failCount} files failed.`);
        } else {
          setUploadSuccess(`Successfully uploaded ${successCount} files from directory.`);
        }
        setUploadFiles([]);
      }
      
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      // Reload directory to show new files
      loadDirectory(currentPath);
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes?: number): string => {
    if (bytes === undefined || bytes === null) return '-';
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return '-';
    }
  };

  const getBreadcrumbs = (): string[] => {
    if (!currentPath || currentPath === '') return [];
    return currentPath.split('/').filter(p => p);
  };

  const handleCopyPath = (item: FileInfo) => {
    const fullPath = `/app/data/${item.path}`;
    navigator.clipboard.writeText(fullPath).then(() => {
      setUploadSuccess(`Path copied: ${fullPath}`);
      setTimeout(() => setUploadSuccess(null), 3000);
    }).catch(() => {
      setUploadError('Failed to copy path to clipboard');
      setTimeout(() => setUploadError(null), 3000);
    });
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setCreateFolderError('Folder name cannot be empty');
      return;
    }

    setCreateFolderError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/files/folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: currentPath,
          folder_name: newFolderName.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create folder');
      }

      setShowCreateFolder(false);
      setNewFolderName('');
      setUploadSuccess('Folder created successfully');
      setTimeout(() => setUploadSuccess(null), 3000);
      loadDirectory(currentPath);
    } catch (err: any) {
      setCreateFolderError(err?.message || 'Failed to create folder');
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItem) return;

    setDeleteError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/files?path=${encodeURIComponent(deleteItem.path)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete');
      }

      setShowDeleteConfirm(false);
      setDeleteItem(null);
      setUploadSuccess(`${deleteItem.is_directory ? 'Folder' : 'File'} deleted successfully`);
      setTimeout(() => setUploadSuccess(null), 3000);
      loadDirectory(currentPath);
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete');
    }
  };

  const handleMoveItem = async () => {
    if (!moveSourceItem) return;

    setMoveError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/files/move`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_path: moveSourceItem.path,
          destination_path: moveDestPath,
          new_name: moveNewName.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to move/rename');
      }

      setShowMoveDialog(false);
      setMoveSourceItem(null);
      setMoveDestPath('');
      setMoveNewName('');
      setUploadSuccess('Item moved/renamed successfully');
      setTimeout(() => setUploadSuccess(null), 3000);
      loadDirectory(currentPath);
    } catch (err: any) {
      setMoveError(err?.message || 'Failed to move/rename');
    }
  };

  const openMoveDialog = (item: FileInfo) => {
    setMoveSourceItem(item);
    setMoveDestPath(currentPath);
    setMoveNewName('');
    setMoveError(null);
    setShowMoveDialog(true);
    setActiveMenuItemPath(null);
  };

  const openDeleteConfirm = (item: FileInfo) => {
    setDeleteItem(item);
    setDeleteError(null);
    setShowDeleteConfirm(true);
    setActiveMenuItemPath(null);
  };

  const handleDownload = async (item: FileInfo) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/files/download?path=${encodeURIComponent(item.path)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download');
      }

      // Get the filename from Content-Disposition header or use the item name
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = item.name;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      // Add .zip extension for directories if not already present
      if (item.is_directory && !filename.endsWith('.zip')) {
        filename = `${filename}.zip`;
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setUploadSuccess(`Downloaded ${item.is_directory ? 'directory' : 'file'}: ${item.name}`);
      setTimeout(() => setUploadSuccess(null), 3000);
    } catch (err: any) {
      setUploadError(err?.message || 'Failed to download');
      setTimeout(() => setUploadError(null), 3000);
    }
  };

  if (loading && !directoryListing) {
    return (
      <div style={{ padding: 24, color: theme.text }}>
        Loading directory...
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>Data Directory</h1>
        <button
          onClick={() => setShowCreateFolder(true)}
          style={{
            padding: '8px 16px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <FolderPlus size={18} />
          New Folder
        </button>
      </div>
      <p style={{ color: theme.textSecondary, marginBottom: 24 }}>
        Browse, upload, and manage files in the data directory
      </p>

      {/* Breadcrumb Navigation */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => handleNavigate('')}
          style={{
            padding: '6px 12px',
            background: currentPath === '' ? theme.activeBackground : 'transparent',
            color: currentPath === '' ? theme.activeText : theme.text,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Home size={16} />
          data
        </button>

        {getBreadcrumbs().map((part, index) => {
          const pathUpToHere = getBreadcrumbs().slice(0, index + 1).join('/');
          const isLast = index === getBreadcrumbs().length - 1;
          
          return (
            <React.Fragment key={index}>
              <ChevronRight size={16} color={theme.textSecondary} />
              <button
                onClick={() => handleNavigate(pathUpToHere)}
                style={{
                  padding: '6px 12px',
                  background: isLast ? theme.activeBackground : 'transparent',
                  color: isLast ? theme.activeText : theme.text,
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {part}
              </button>
            </React.Fragment>
          );
        })}

        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => loadDirectory(currentPath)}
            disabled={loading}
            style={{
              padding: '6px 12px',
              background: theme.buttonBackground,
              color: theme.buttonText,
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 14,
              fontWeight: 600,
              opacity: loading ? 0.6 : 1,
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div style={{
        background: theme.cardBackground,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
      }}>
        <h3 style={{ color: theme.text, fontSize: 16, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Upload size={18} />
          Upload {uploadMode === 'file' ? 'File' : 'Directory'}
        </h3>
        
        {/* Mode Selection */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button
            onClick={() => {
              setUploadMode('file');
              setUploadFile(null);
              setUploadFiles([]);
              const fileInput = document.getElementById('file-upload') as HTMLInputElement;
              if (fileInput) fileInput.value = '';
            }}
            style={{
              padding: '6px 12px',
              background: uploadMode === 'file' ? theme.buttonBackground : theme.background,
              color: uploadMode === 'file' ? theme.buttonText : theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Single File
          </button>
          <button
            onClick={() => {
              setUploadMode('directory');
              setUploadFile(null);
              setUploadFiles([]);
              const fileInput = document.getElementById('file-upload') as HTMLInputElement;
              if (fileInput) fileInput.value = '';
            }}
            style={{
              padding: '6px 12px',
              background: uploadMode === 'directory' ? theme.buttonBackground : theme.background,
              color: uploadMode === 'directory' ? theme.buttonText : theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Directory
          </button>
        </div>
        
        {/* Folder Selection */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 6, color: theme.text, fontWeight: 600, fontSize: 13 }}>
            Upload to folder:
          </label>
          <select
            value={uploadTargetFolder}
            onChange={(e) => setUploadTargetFolder(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              background: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            <option value="">Current Directory ({currentPath || '/app/data'})</option>
            {directoryListing?.items
              .filter(item => item.is_directory)
              .map((folder, index) => (
                <option key={index} value={folder.name}>
                  📁 {folder.name}
                </option>
              ))}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileSelect}
            multiple={uploadMode === 'file'}
            {...(uploadMode === 'directory' ? { webkitdirectory: '', directory: '', mozdirectory: '' } as any : {})}
            style={{
              padding: '8px',
              background: theme.background,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: 4,
              flex: 1,
            }}
          />
          <button
            onClick={handleUpload}
            disabled={uploadFiles.length === 0 || uploading}
            style={{
              padding: '8px 16px',
              background: uploadFiles.length > 0 && !uploading ? theme.buttonBackground : theme.border,
              color: uploadFiles.length > 0 && !uploading ? theme.buttonText : theme.textSecondary,
              border: 'none',
              borderRadius: 4,
              cursor: uploadFiles.length > 0 && !uploading ? 'pointer' : 'not-allowed',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>

        {uploadFiles.length > 0 && (
          <div style={{
            background: theme.background,
            border: `1px solid ${theme.border}`,
            padding: 8,
            borderRadius: 4,
            fontSize: 13,
            color: theme.text,
            marginBottom: 12,
          }}>
            Selected: {uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''}
          </div>
        )}

        {uploadError && (
          <div style={{
            background: theme.errorLight,
            color: theme.error,
            border: `1px solid ${theme.error}`,
            padding: 8,
            borderRadius: 4,
            fontSize: 13,
          }}>
            {uploadError}
          </div>
        )}

        {uploadSuccess && (
          <div style={{
            background: '#d1fae5',
            color: '#065f46',
            border: '1px solid #10b981',
            padding: 8,
            borderRadius: 4,
            fontSize: 13,
          }}>
            {uploadSuccess}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div style={{
          background: theme.errorLight,
          color: theme.error,
          border: `1px solid ${theme.error}`,
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {/* Directory Listing */}
      {directoryListing && (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            width: '98%',
            display: 'grid',
            gridTemplateColumns: '1fr 120px 180px 280px',
            gap: 12,
            padding: '12px 16px',
            background: theme.background,
            borderBottom: `1px solid ${theme.border}`,
            fontWeight: 700,
            fontSize: 13,
            color: theme.textSecondary,
            textTransform: 'uppercase',
          }}>
            <div>Name</div>
            <div>Size</div>
            <div>Modified</div>
            <div style={{ textAlign: 'right' }}>Actions</div>
          </div>

          {/* Parent Directory Link */}
          {directoryListing.parent_path !== null && directoryListing.parent_path !== undefined && (
            <div
              onClick={() => handleNavigate(directoryListing.parent_path || '')}
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr 120px 180px 280px',
                gap: 12,
                padding: '12px 16px',
                background: 'transparent',
                borderBottom: `1px solid ${theme.border}`,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.background}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.text }}>
                <Folder size={18} color={theme.accent} />
                <span style={{ fontWeight: 600 }}>.. (Parent Directory)</span>
              </div>
              <div style={{ color: theme.textSecondary }}>-</div>
              <div style={{ color: theme.textSecondary }}>-</div>
              <div></div>
            </div>
          )}

          {/* Items */}
          {directoryListing.items.length === 0 ? (
            <div style={{
              padding: 48,
              textAlign: 'center',
              color: theme.textSecondary,
              fontSize: 14,
            }}>
              This directory is empty
            </div>
          ) : (
            directoryListing.items.map((item, index) => (
              <div
                key={index}
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '1fr 120px 180px 280px',
                  gap: 12,
                  padding: '12px 16px',
                  background: 'transparent',
                  borderBottom: index < directoryListing.items.length - 1 ? `1px solid ${theme.border}` : 'none',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.background}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div 
                  onClick={() => item.is_directory ? handleNavigate(item.path) : undefined}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8, 
                    color: theme.text,
                    cursor: item.is_directory ? 'pointer' : 'default'
                  }}
                >
                  {item.is_directory ? (
                    <Folder size={18} color={theme.accent} />
                  ) : (
                    <File size={18} color={theme.textSecondary} />
                  )}
                  <span style={{ fontWeight: item.is_directory ? 600 : 400 }}>
                    {item.name}
                  </span>
                </div>
                <div style={{ color: theme.textSecondary, fontSize: 13, display: 'flex', alignItems: 'center' }}>
                  {formatSize(item.size)}
                </div>
                <div style={{ color: theme.textSecondary, fontSize: 13, display: 'flex', alignItems: 'center' }}>
                  {formatDate(item.modified_time)}
                </div>
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button
                    onClick={() => handleCopyPath(item)}
                    title="Copy path"
                    style={{
                      padding: '6px 10px',
                      background: theme.cardBackground,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Copy size={14} />
                    Copy Path
                  </button>
                  <button
                    onClick={() => openMoveDialog(item)}
                    title="Move/Rename"
                    style={{
                      padding: '6px 10px',
                      background: theme.cardBackground,
                      color: theme.text,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Move size={14} />
                  </button>
                  <button
                    onClick={() => handleDownload(item)}
                    title={item.is_directory ? "Download as ZIP" : "Download"}
                    style={{
                      padding: '6px 10px',
                      background: theme.buttonBackground,
                      color: theme.buttonText,
                      border: 'none',
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Download size={14} />
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(item)}
                    title="Delete"
                    style={{
                      padding: '6px 10px',
                      background: theme.errorLight,
                      color: theme.error,
                      border: `1px solid ${theme.error}`,
                      borderRadius: 4,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Create Folder Dialog */}
      {showCreateFolder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 24,
            minWidth: 400,
            maxWidth: 500,
          }}>
            <h3 style={{ color: theme.text, marginTop: 0, marginBottom: 16 }}>Create New Folder</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, color: theme.text, fontWeight: 600 }}>
                Folder Name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateFolder();
                  if (e.key === 'Escape') {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                    setCreateFolderError(null);
                  }
                }}
                style={{
                  width: '100%',
                  padding: 10,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  background: theme.background,
                  color: theme.text,
                  fontSize: 14,
                }}
              />
            </div>
            {createFolderError && (
              <div style={{
                background: theme.errorLight,
                color: theme.error,
                border: `1px solid ${theme.error}`,
                padding: 10,
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 13,
              }}>
                {createFolderError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowCreateFolder(false);
                  setNewFolderName('');
                  setCreateFolderError(null);
                }}
                style={{
                  padding: '8px 16px',
                  background: theme.cardBackground,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                style={{
                  padding: '8px 16px',
                  background: newFolderName.trim() ? theme.buttonBackground : theme.border,
                  color: newFolderName.trim() ? theme.buttonText : theme.textSecondary,
                  border: 'none',
                  borderRadius: 6,
                  cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move/Rename Dialog */}
      {showMoveDialog && moveSourceItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 24,
            minWidth: 500,
            maxWidth: 600,
          }}>
            <h3 style={{ color: theme.text, marginTop: 0, marginBottom: 16 }}>
              Move/Rename: {moveSourceItem.name}
            </h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, color: theme.text, fontWeight: 600 }}>
                Destination Path
              </label>
              <input
                type="text"
                value={moveDestPath}
                onChange={(e) => setMoveDestPath(e.target.value)}
                placeholder="Enter destination path (leave empty for root)"
                style={{
                  width: '100%',
                  padding: 10,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  background: theme.background,
                  color: theme.text,
                  fontSize: 14,
                }}
              />
              <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 4 }}>
                Current: {currentPath || '(root)'}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, color: theme.text, fontWeight: 600 }}>
                New Name (optional)
              </label>
              <input
                type="text"
                value={moveNewName}
                onChange={(e) => setMoveNewName(e.target.value)}
                placeholder={`Leave empty to keep: ${moveSourceItem.name}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleMoveItem();
                  if (e.key === 'Escape') {
                    setShowMoveDialog(false);
                    setMoveSourceItem(null);
                    setMoveDestPath('');
                    setMoveNewName('');
                    setMoveError(null);
                  }
                }}
                style={{
                  width: '100%',
                  padding: 10,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  background: theme.background,
                  color: theme.text,
                  fontSize: 14,
                }}
              />
            </div>
            {moveError && (
              <div style={{
                background: theme.errorLight,
                color: theme.error,
                border: `1px solid ${theme.error}`,
                padding: 10,
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 13,
              }}>
                {moveError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowMoveDialog(false);
                  setMoveSourceItem(null);
                  setMoveDestPath('');
                  setMoveNewName('');
                  setMoveError(null);
                }}
                style={{
                  padding: '8px 16px',
                  background: theme.cardBackground,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMoveItem}
                style={{
                  padding: '8px 16px',
                  background: theme.buttonBackground,
                  color: theme.buttonText,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Move/Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && deleteItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.cardBackground,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            padding: 24,
            minWidth: 400,
            maxWidth: 500,
          }}>
            <h3 style={{ color: theme.text, marginTop: 0, marginBottom: 16 }}>
              Confirm Delete
            </h3>
            <p style={{ color: theme.text, marginBottom: 16 }}>
              Are you sure you want to delete <strong>{deleteItem.name}</strong>?
              {deleteItem.is_directory && (
                <span style={{ display: 'block', marginTop: 8, color: theme.error, fontWeight: 600 }}>
                  Warning: This will delete the folder and all its contents!
                </span>
              )}
            </p>
            {deleteError && (
              <div style={{
                background: theme.errorLight,
                color: theme.error,
                border: `1px solid ${theme.error}`,
                padding: 10,
                borderRadius: 6,
                marginBottom: 16,
                fontSize: 13,
              }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteItem(null);
                  setDeleteError(null);
                }}
                style={{
                  padding: '8px 16px',
                  background: theme.cardBackground,
                  color: theme.text,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteItem}
                style={{
                  padding: '8px 16px',
                  background: theme.error,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
